// ============================================================================
// FAILURE LIBRARY ENGINE
// Auto-suggest and template application from 769 equipment types, 22,407 failure modes
// Bridges the built-in Optimal Asset Maintenance Reference Library into workflows
// ============================================================================

let _libraryIndex = null;
let _loadingPromise = null;

// ---------------------------------------------------------------------------
// LOAD LIBRARY INDEX (lazy, cached)
// ---------------------------------------------------------------------------
async function loadLibraryIndex() {
  if (_libraryIndex) return _libraryIndex;
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = fetch('./data/failureLibrary-index.json')
    .then(r => r.json())
    .then(data => {
      _libraryIndex = data;
      _loadingPromise = null;
      return data;
    })
    .catch(err => {
      console.warn('Failed to load failure library index:', err);
      _loadingPromise = null;
      return { categories: {}, metadata: {} };
    });

  return _loadingPromise;
}

// ---------------------------------------------------------------------------
// SEARCH LIBRARY — fuzzy match equipment type against 769 entries
// Returns ranked matches with metadata
// ---------------------------------------------------------------------------
async function searchLibrary(query, maxResults = 10) {
  const index = await loadLibraryIndex();
  if (!index?.categories) return [];

  const q = (query || '').toLowerCase().trim();
  if (!q) return [];

  const results = [];
  Object.entries(index.categories).forEach(([category, entries]) => {
    entries.forEach(entry => {
      const name = (entry.equipmentType || '').toLowerCase();
      const cat = category.toLowerCase();

      // Scoring: exact match > starts with > contains > category match
      let score = 0;
      if (name === q) score = 100;
      else if (name.startsWith(q)) score = 80;
      else if (name.includes(q)) score = 60;
      else if (cat === q) score = 50;
      else if (cat.startsWith(q)) score = 40;
      else if (cat.includes(q)) score = 30;
      else {
        // Word-level matching
        const qWords = q.split(/[\s,_-]+/).filter(w => w.length > 2);
        const nameWords = name.split(/[\s,_-]+/);
        const matched = qWords.filter(w => nameWords.some(nw => nw.includes(w)));
        if (matched.length > 0) score = 20 + (matched.length / qWords.length) * 30;
      }

      if (score > 0) {
        results.push({
          ...entry,
          category,
          score,
          hasFailureModes: entry.failureModeCount > 0,
          hasTasks: entry.taskCount > 0,
          hasStrategies: entry.strategies?.length > 0
        });
      }
    });
  });

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
}

// ---------------------------------------------------------------------------
// LOAD EQUIPMENT TEMPLATE — full failure mode, task, strategy data for a type
// ---------------------------------------------------------------------------
async function loadEquipmentTemplate(fileName) {
  try {
    const response = await fetch(`./data/failure-library/${fileName}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data;
  } catch (err) {
    console.warn('Failed to load equipment template:', fileName, err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// APPLY TEMPLATE TO PROJECT — creates failure modes, tasks, and resources
// from a library template entry
// ---------------------------------------------------------------------------
function applyTemplateToProject(template, equipmentId, equipmentDescription, options = {}) {
  if (!template?.components) return { failuremodes: [], scheduledtasks: [], correctivetasks: [] };

  const {
    includeFailureModes = true,
    includeTasks = true,
    includeStrategies = true,
    prefix = ''
  } = options;

  const timestamp = Date.now();
  let fmIdx = 0;
  let taskIdx = 0;

  const failuremodes = [];
  const scheduledtasks = [];
  const correctivetasks = [];

  Object.entries(template.components).forEach(([componentName, componentData]) => {
    // Create failure modes
    if (includeFailureModes && componentData.failureModes) {
      componentData.failureModes.forEach(fm => {
        fmIdx++;
        const fmId = `${prefix}FM-${timestamp}-${String(fmIdx).padStart(3, '0')}`;
        failuremodes.push({
          id: fmId,
          description: `${componentName}: ${fm.what || componentName}`,
          component: componentName,
          mechanism: fm.mechanism || '',
          cause: fm.cause || '',
          equipmentId: equipmentId,
          equipment: equipmentDescription || equipmentId,
          severity: fm.severity || 5,
          spareRequirement: '',
          failureRate: fm.failureRate || '',
          consequence: fm.consequence || '',
          consequenceOfFailure: fm.consequenceOfFailure || fm.consequence || '',
          functionFailure: fm.functionFailure || '',
          status: fm.status || '',
          strategyType: fm.strategyType || '',
          budgetedAs: fm.budgetedAs || '',
          budgetedLife: fm.budgetedLife || '',
          primaryTask: fm.primaryTask || '',
          primaryTaskType: fm.primaryTaskType || '',
          primaryTaskFrequency: fm.primaryTaskFrequency || '',
          primaryTaskAccessTime: fm.primaryTaskAccessTime || '',
          primaryTaskConstraint: fm.primaryTaskConstraint || '',
          primaryTaskLimits: fm.primaryTaskLimits || '',
          primaryTaskComments: fm.primaryTaskComments || '',
          secondaryTask: fm.secondaryTask || '',
          secondaryTaskType: fm.secondaryTaskType || '',
          secondaryTaskAccessTime: fm.secondaryTaskAccessTime || '',
          secondaryTaskConstraint: fm.secondaryTaskConstraint || '',
          timeUnits: fm.timeUnits || '',
          source: `Library: ${template.equipmentType}`,
          libraryRef: template.equipmentType
        });
      });
    }

    // Create tasks
    if (includeTasks && componentData.tasks) {
      componentData.tasks.forEach(task => {
        taskIdx++;
        const taskId = `${prefix}TSK-${timestamp}-${String(taskIdx).padStart(3, '0')}`;
        const isScheduled = task.interval && task.interval > 0;

        const taskObj = {
          id: taskId,
          description: task.description || `Inspect ${componentName}`,
          component: componentName,
          equipmentId: equipmentId,
          equipment: equipmentDescription || equipmentId,
          interval: task.interval || 0,
          intervalUnit: task.intervalUnit || 'hours',
          timeUnits: task.timeUnits || '',
          constraint: task.constraint || '',
          accessTime: task.primaryTaskAccessTime || task.accessTime || '',
          acceptanceCriteria: task.primaryTaskLimits || task.acceptanceCriteria || '',
          workInstructions: task.primaryTaskComments || task.workInstructions || '',
          strategy: componentData.strategies?.[0] || 'Condition Based',
          strategyType: task.strategyType || '',
          status: task.status || '',
          duration: task.duration || task.primaryTaskAccessTime || '',
          laborReq: task.laborReq || '',
          toolsRequired: task.toolsRequired || '',
          budgetedAs: task.budgetedAs || '',
          budgetedLife: task.budgetedLife || '',
          consequenceOfFailure: task.consequenceOfFailure || '',
          functionFailure: task.functionFailure || '',
          failureMode: task.failureMode || '',
          mechanism: task.mechanism || '',
          // Secondary task passthrough
          secondaryTask: task.secondaryTask || '',
          secondaryTaskType: task.secondaryTaskType || '',
          secondaryTaskAccessTime: task.secondaryTaskAccessTime || '',
          secondaryTaskConstraint: task.secondaryTaskConstraint || '',
          source: `Library: ${template.equipmentType}`,
          libraryRef: template.equipmentType
        };

        if (isScheduled) {
          scheduledtasks.push(taskObj);
        } else {
          correctivetasks.push(taskObj);
        }
      });
    }
  });

  return { failuremodes, scheduledtasks, correctivetasks };
}

// ---------------------------------------------------------------------------
// GET ALL CATEGORIES — for browse/filter UI
// ---------------------------------------------------------------------------
async function getCategories() {
  const index = await loadLibraryIndex();
  if (!index?.categories) return [];

  return Object.entries(index.categories).map(([category, entries]) => ({
    name: category,
    count: entries.length,
    totalFailureModes: entries.reduce((s, e) => s + (e.failureModeCount || 0), 0),
    totalTasks: entries.reduce((s, e) => s + (e.taskCount || 0), 0),
    strategies: [...new Set(entries.flatMap(e => e.strategies || []))]
  })).sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// GET LIBRARY STATS
// ---------------------------------------------------------------------------
async function getLibraryStats() {
  const index = await loadLibraryIndex();
  return index?.metadata || { totalEquipmentTypes: 0, totalRecords: 0, totalUniqueFailureModes: 0 };
}

// ---------------------------------------------------------------------------
// SFG20 LIBRARY BRIDGE — apply SFG20 schedule as template
// ---------------------------------------------------------------------------
function applySFG20Template(schedule, equipmentId, equipmentDescription) {
  if (!schedule?.tasks) return { scheduledtasks: [] };

  const timestamp = Date.now();
  const scheduledtasks = schedule.tasks.map((task, idx) => ({
    id: `SFG-${timestamp}-${String(idx + 1).padStart(3, '0')}`,
    description: `[SFG20 ${schedule.ref}] ${task.item || task.description || ''}`,
    action: task.action || '',
    component: task.item || '',
    equipmentId: equipmentId,
    equipment: equipmentDescription || equipmentId,
    frequency: task.frequency || '',
    interval: parseFrequencyToHours(task.frequency),
    intervalUnit: 'hours',
    skillSet: task.skillSet || '',
    constraint: task.constraint || '',
    strategy: 'Fixed Time',
    source: `SFG20 ${schedule.ref} — ${schedule.title}`,
    sfg20Ref: schedule.ref,
    libraryRef: `SFG20:${schedule.ref}`
  }));

  return { scheduledtasks };
}

// Convert SFG20 frequency text to approximate hours
function parseFrequencyToHours(freq) {
  if (!freq) return 8760;
  const f = freq.toLowerCase();
  if (f.includes('daily')) return 24;
  if (f.includes('weekly')) return 168;
  if (f.includes('fortnightly') || f.includes('bi-weekly')) return 336;
  if (f.includes('monthly')) return 720;
  if (f.includes('quarterly') || f.includes('3 month')) return 2190;
  if (f.includes('6 month') || f.includes('semi-annual') || f.includes('biannual')) return 4380;
  if (f.includes('annual') || f.includes('yearly') || f.includes('12 month')) return 8760;
  if (f.includes('2 year') || f.includes('biennial')) return 17520;
  if (f.includes('3 year')) return 26280;
  if (f.includes('5 year')) return 43800;
  return 8760;
}

// ---------------------------------------------------------------------------
// EXPORTS
// ---------------------------------------------------------------------------
export {
  searchLibrary,
  loadEquipmentTemplate,
  applyTemplateToProject,
  getCategories,
  getLibraryStats,
  loadLibraryIndex,
  applySFG20Template,
  parseFrequencyToHours
};
